import { useMemo } from 'react';
import { Alert, Card, Flex, Tag, Typography } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import OnboardingShell from '../../components/OnboardingShell';
import { useOnboarding } from '../../context/OnboardingContext';
import { useCard } from '../../context/CardContext';
import { buildBotPath } from '../../utils/routes';
import { formatCurrency, formatTariffDuration } from '../../utils/format';
import { buildTariffSavings } from '../../utils/tariffSavings';
import type { RewardReason } from '../../types/subscription';

const discountReasonText = (reason: RewardReason) => {
    switch (reason) {
        case 'invitee':
            return "Do'stingiz taklifi bilan kelganingiz Sizga birinchi muddatga chegirma berildi";
        case 'deep_link':
            return "Maxsus havola orqali kelganingiz uchun birinchi to'lovga chegirma berildi";
        default:
            return "Siz do'stingizni taklif qildingiz";
    }
};

const TarrifPage = () => {
    const navigate = useNavigate();
    const { bot_id } = useParams();
    const { subscriptionInfo } = useOnboarding();
    const { saveSelectedTariff } = useCard();
    const tariffs = useMemo(
        () => [...(subscriptionInfo?.tariffs || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
        [subscriptionInfo],
    );
    // How much a longer plan beats buying the shortest one over and over. Computed from the regular
    // prices, so a pending first-payment discount never distorts it.
    const { base, savings } = useMemo(() => buildTariffSavings(tariffs), [tariffs]);
    const baseDuration = base
        ? formatTariffDuration(base.subscription_type, base.period_count, base.period_seconds)
        : null;
    const discountBannerText = tariffs.find((tariff) => tariff.discount)?.discount?.reason
        ? discountReasonText(tariffs.find((tariff) => tariff.discount)!.discount!.reason)
        : null;
    // Extra time is not a price, so it cannot be shown as a struck-out amount on the cards — it gets
    // its own line above them instead.
    const bonusTime = subscriptionInfo?.bonus_time ?? null;

    const goToPayment = (tariff: typeof tariffs[number]) => {
        saveSelectedTariff(tariff);
        navigate(buildBotPath(bot_id, '/onboarding/offer'));
    };

    return (
        <OnboardingShell
            title="Tarifni tanlang"
            subtitle="Sizga mos obuna rejasini tanlang."
        >
            <Flex vertical gap={16}>
                <Typography.Paragraph className="section-text">
                    To'lovlar ogohlantiruvchi xabardan so'ng tanlangan tarif reja asosida avtomatik o'z vaqtida yechib olinadi. Agar karta bloklangan bo'lsa yoki mablag' yetarli bo'lmasa obunangiz to'xtatiladi. Obunani istalgan vaqt bekor qilishingiz mumkin
                </Typography.Paragraph>
                {discountBannerText && (
                    <Typography.Paragraph className="section-text" type="secondary">
                        {discountBannerText}
                    </Typography.Paragraph>
                )}
                {bonusTime && bonusTime.hours > 0 && (
                    <Alert
                        type="success"
                        showIcon
                        message={`Birinchi to'lovdan so'ng obunangizga +${bonusTime.hours} soat qo'shiladi`}
                    />
                )}
                {tariffs.length > 0 ? tariffs.map((tariff) => {
                    const saving = savings[tariff.id];

                    return (
                        <Card
                            key={tariff.id}
                            className="info-panel tariff-card"
                            bordered={false}
                            hoverable
                            role="button"
                            tabIndex={0}
                            aria-label={`${tariff.title} — ${formatCurrency(tariff.amount, tariff.currency)}${saving ? ` — ${saving.percentOff}% tejash` : ''}`}
                            onClick={() => goToPayment(tariff)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    goToPayment(tariff);
                                }
                            }}
                        >
                            <Flex vertical gap={8}>
                                <Flex align="center" justify="space-between" gap={12}>
                                    <Flex vertical gap={4} style={{ minWidth: 0 }}>
                                        <Typography.Title level={5} className="section-title">
                                            {tariff.title}
                                        </Typography.Title>
                                        <Typography.Text className="section-text">
                                            {formatTariffDuration(tariff.subscription_type, tariff.period_count, tariff.period_seconds)}
                                        </Typography.Text>
                                    </Flex>
                                    <Flex vertical align="end" gap={4}>
                                        {tariff.discount && (
                                            <Typography.Text delete type="secondary" className="tariff-card__list-price">
                                                {formatCurrency(tariff.list_amount, tariff.currency)}
                                            </Typography.Text>
                                        )}
                                        <Flex align="center" gap={8}>
                                            <Typography.Text className="price-title">
                                                {formatCurrency(tariff.amount, tariff.currency)}
                                            </Typography.Text>
                                            <RightOutlined className="tariff-card__chevron" />
                                        </Flex>
                                        {(tariff.discount || saving) && (
                                            <Flex align="center" justify="end" gap={4} wrap="wrap">
                                                {tariff.discount && (
                                                    <Tag color="success" style={{ marginInlineEnd: 0 }}>
                                                        −{tariff.discount.percent_off}%
                                                    </Tag>
                                                )}
                                                {/* Blue, not green: both tags can sit on one card and two green
                                                    −N% chips would read as the same number twice. */}
                                                {saving && (
                                                    <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                                                        −{saving.percentOff}% tejash
                                                    </Tag>
                                                )}
                                            </Flex>
                                        )}
                                    </Flex>
                                </Flex>
                                {saving && baseDuration && (
                                    <Typography.Text type="secondary" className="tariff-card__savings">
                                        {saving.multiplier} × {baseDuration} ={' '}
                                        <Typography.Text delete type="secondary" className="tariff-card__savings">
                                            {formatCurrency(saving.baselineTotal, tariff.currency)}
                                        </Typography.Text>
                                        {' · '}
                                        {formatCurrency(saving.saved, tariff.currency)} tejaysiz
                                    </Typography.Text>
                                )}
                            </Flex>
                        </Card>
                    );
                }) : (
                    <Card className="info-panel" bordered={false}>
                        <Typography.Paragraph className="section-text">
                            Hozircha faol tariflar topilmadi.
                        </Typography.Paragraph>
                    </Card>
                )}
            </Flex>
        </OnboardingShell>
    );
};

export default TarrifPage;
